import {
  ActionIcon,
  Button,
  Select,
  ScrollArea,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { Plus, X as XIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import classes from '../point-note-detail/page.module.css';

import { useQueriesWithGlobal } from '@data-access';
import {
  fieldsQueries,
  flattenIlotSamples,
  pointsApi,
  resolveMapPointScope,
} from '@fields/data-access';
import { FullMainHeight } from '@ui';

type LocalImage = {
  id: string;
  previewUrl: string;
  file: File;
};

export function PointImpossiblePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate({ from: '/map-impossible' });
  const search = useSearch({ from: '/_app/map-impossible' });
  const queries = useQueriesWithGlobal(fieldsQueries);
  const scope = resolveMapPointScope(search.scope);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [localImages, setLocalImages] = useState<LocalImage[]>([]);
  const [removedExistingImage, setRemovedExistingImage] = useState(false);
  const initializedFromExistingRef = useRef(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const trimmedTitle = title.trim();
  const trimmedContent = content.trim();
  const titleError = submitAttempted && !trimmedTitle;
  const contentError = submitAttempted && !trimmedContent;
  const ilotDetail = useQuery({
    ...queries.ilotDetail(search.ilotId, scope),
    enabled: Boolean(search.ilotId),
  });
  const pointValuesQuery = useQuery({
    queryKey: ['points', 'values', i18n.language],
    queryFn: () => pointsApi.getPointsValues(),
  });
  const impossibleTitleOptions = useMemo(
    () =>
      (pointValuesQuery.data?.title ?? []).map((item) => ({
        value: item.value,
        label: item.label,
      })),
    [pointValuesQuery.data?.title],
  );
  const existingPoint = useMemo(
    () =>
      flattenIlotSamples(ilotDetail.data?.samples ?? []).find(
        (point) => point.id === search.pointId,
      ),
    [ilotDetail.data?.samples, search.pointId],
  );
  const existingImpossibleImagePath =
    removedExistingImage ? null : existingPoint?.properties.impossible_image ?? null;
  const getBackendFileUrl = (maybePath: string | null) => {
    if (!maybePath) return null;
    if (maybePath.startsWith('http://') || maybePath.startsWith('https://')) {
      return maybePath;
    }

    const apiBase = String((import.meta as { env?: { VITE_API_URL?: string } }).env?.VITE_API_URL ?? '');
    if (!apiBase) return maybePath;
    const backendBase = apiBase.replace(/\/api\/?$/, '');
    const normalized = maybePath.startsWith('/') ? maybePath : `/${maybePath}`;
    return `${backendBase}${normalized}`;
  };
  const existingImpossibleImageUrl = getBackendFileUrl(existingImpossibleImagePath);
  const hasAnyImage = Boolean(existingImpossibleImageUrl || localImages.length);
  const imageError = submitAttempted && !hasAnyImage;
  const isFormValid = Boolean(trimmedTitle && trimmedContent && hasAnyImage);

  useEffect(() => {
    if (initializedFromExistingRef.current) return;
    if (!existingPoint) return;

    setTitle(existingPoint.properties.impossible_title ?? '');
    setContent(existingPoint.properties.impossible_content ?? '');
    initializedFromExistingRef.current = true;
  }, [existingPoint]);

  const saveImpossibleMutation = useMutation({
    mutationFn: () =>
      pointsApi.patchPointImpossible({
        id: search.pointId,
        title: trimmedTitle,
        content: trimmedContent,
        image: localImages[0]?.file ?? null,
        scope,
      }),
    onSuccess: () => {
      navigate({
        to: '/map',
        search: {
          ilotId: search.ilotId,
          pointId: search.pointId,
          pointCode: search.pointCode,
          sampleId: search.sampleId,
          lng: search.lng,
          lat: search.lat,
          zoom: search.zoom,
        scope: search.scope,
        },
      });
    },
  });
  const handleClose = () => {
    navigate({
      to: '/map',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  const handleCancel = () => {
    navigate({
      to: '/map',
      search: {
        ilotId: search.ilotId,
        pointId: search.pointId,
        pointCode: search.pointCode,
        sampleId: search.sampleId,
        lng: search.lng,
        lat: search.lat,
        zoom: search.zoom,
        scope: search.scope,
      },
    });
  };

  const handlePickImage = () => {
    fileInputRef.current?.click();
  };

  const handleAddImage = (e: { currentTarget: HTMLInputElement }) => {
    const firstFile = e.currentTarget.files?.[0];
    if (!firstFile) return;

    const nextImage = {
      id: `${firstFile.name}-${firstFile.lastModified}-${Math.random().toString(16).slice(2)}`,
      file: firstFile,
      previewUrl: URL.createObjectURL(firstFile),
    };

    setRemovedExistingImage(true);
    setLocalImages((prev) => {
      prev.forEach((img) => URL.revokeObjectURL(img.previewUrl));
      return [nextImage];
    });
    e.currentTarget.value = '';
  };

  const handleRemoveLocalImage = (id: string) => {
    setLocalImages((prev) => {
      const image = prev.find((img) => img.id === id);
      if (image) URL.revokeObjectURL(image.previewUrl);
      return prev.filter((img) => img.id !== id);
    });
  };

  useEffect(() => {
    return () => {
      localImages.forEach((img) => URL.revokeObjectURL(img.previewUrl));
    };
  }, [localImages]);

  return (
    <FullMainHeight.Root>
      <FullMainHeight.Content className={classes.content}>
        <Stack gap={0} h="100%">
          <header className={classes.header}>
            <Text className={classes.headerTitle}>{t('sampling.impossible.title')}</Text>
            <ActionIcon
              aria-label={t('sampling.impossible.closeAria')}
              color="dark"
              onClick={handleClose}
              radius="xl"
              size={32}
              variant="light"
            >
              <XIcon size={18} />
            </ActionIcon>
          </header>

          <ScrollArea h="100%">
            <Stack className={classes.stack} gap="md">
              <Stack gap="sm">
                <Select
                  data={impossibleTitleOptions}
                  fw={700}
                  onChange={(value) => setTitle(value ?? '')}
                  placeholder={t('sampling.impossible.titlePlaceholder')}
                  searchable={false}
                  value={title || null}
                />
                <span className={classes.greenLine} />
                {titleError ? (
                  <Text c="red" size="xs">
                    {t('sampling.impossible.titleRequired')}
                  </Text>
                ) : null}

                <Textarea
                  autosize
                  className={classes.contentInput}
                  minRows={5}
                  onChange={(e) => setContent(e.currentTarget.value)}
                  placeholder={t('sampling.notes.commentPlaceholder')}
                  value={content}
                />
                {contentError ? (
                  <Text c="red" size="xs">
                    {t('sampling.notes.commentRequired')}
                  </Text>
                ) : null}

                <span className={classes.greenLine} />

                <div className={classes.mediaRow}>
                  {existingImpossibleImageUrl ? (
                    <div className={classes.thumbWrap}>
                      <img
                        alt={t('sampling.impossible.imageAlt')}
                        className={classes.thumbImage}
                        src={existingImpossibleImageUrl}
                      />
                      <ActionIcon
                        aria-label={t('sampling.impossible.removeExistingPhotoAria')}
                        className={classes.thumbDelete}
                        onClick={() => setRemovedExistingImage(true)}
                        radius="xl"
                        size={22}
                        variant="filled"
                      >
                        <XIcon size={12} />
                      </ActionIcon>
                    </div>
                  ) : null}
                  {localImages.map((image) => (
                    <div className={classes.thumbWrap} key={image.id}>
                      <img alt={t('sampling.impossible.newAttachmentAlt')} className={classes.thumbImage} src={image.previewUrl} />
                      <ActionIcon
                        aria-label={t('sampling.notes.removePhotoAria')}
                        className={classes.thumbDelete}
                        onClick={() => handleRemoveLocalImage(image.id)}
                        radius="xl"
                        size={22}
                        variant="filled"
                      >
                        <XIcon size={12} />
                      </ActionIcon>
                    </div>
                  ))}

                  {!hasAnyImage ? (
                    <button className={classes.addPhotoTile} onClick={handlePickImage} type="button">
                      <Plus size={22} />
                      <span>{t('sampling.notes.takePhoto')}</span>
                    </button>
                  ) : null}
                  {imageError ? (
                    <Text c="red" size="xs">
                      {t('sampling.impossible.photoRequired')}
                    </Text>
                  ) : null}
                  <input
                    accept="image/*"
                    capture="environment"
                    onChange={handleAddImage}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    type="file"
                  />
                </div>
              </Stack>
            </Stack>
          </ScrollArea>

          <div className={classes.footerSticky}>
            <div className={classes.footerActions}>
              <Button className={classes.cancelButton} onClick={handleCancel} radius="lg">
                {t('common.cancel')}
              </Button>
              <Button
                className={classes.saveButton}
                disabled={!isFormValid || saveImpossibleMutation.isPending}
                loading={saveImpossibleMutation.isPending}
                onClick={() => {
                  setSubmitAttempted(true);
                  if (!isFormValid) return;
                  saveImpossibleMutation.mutate();
                }}
                radius="lg"
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </Stack>
      </FullMainHeight.Content>
    </FullMainHeight.Root>
  );
}

export default PointImpossiblePage;
